import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select } from '../components/ui/select';
import { Target, CheckCircle, ArrowRight, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ImplementationPlanning = () => {
  const [selectedStrategy, setSelectedStrategy] = useState('QRM');
  const [userProfile, setUserProfile] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('planning');

  useEffect(() => {
    // Get user profile from localStorage or API
    const userId = localStorage.getItem('userId');
    if (userId) {
      // In a real app, fetch user profile from API
      setUserProfile({
        company_name: 'Your Company',
        sector: 'manufacturing',
        maturity_level: 'some-experience'
      });
    }
  }, []);

  const implementationPhases = {
    QRM: [
      {
        phase: 'Phase 1: Foundation (Weeks 1-4)',
        duration: '4 weeks',
        tasks: [
          'QRM Bronze certification training for key team members',
          'Current state assessment and MCT mapping',
          'Identify pilot manufacturing cell',
          'Form QROC (QRM Oversight Committee)',
          'Baseline measurements setup'
        ],
        deliverables: ['QRM readiness assessment', 'Pilot cell selection', 'Current state MCT map'],
        resources: ['2-3 team leaders', '1 process engineer', 'External QRM consultant (optional)']
      },
      {
        phase: 'Phase 2: Pilot Implementation (Weeks 5-12)',
        duration: '8 weeks',
        tasks: [
          'Implement POLCA system in pilot cell',
          'Cross-training program for cell operators',
          'Setup visual management systems',
          'Implement quick changeover techniques',
          'Weekly QROC meetings and progress tracking'
        ],
        deliverables: ['POLCA cards system', 'Cross-training matrix', 'Visual boards'],
        resources: ['Cell operators', 'Maintenance team', 'IT support for tracking systems']
      },
      {
        phase: 'Phase 3: Optimization (Weeks 13-20)',
        duration: '8 weeks',
        tasks: [
          'Fine-tune POLCA parameters',
          'Implement office QRM for order processing',
          'Expand to second manufacturing cell',
          'Develop internal QRM champions',
          'Measure and celebrate early wins'
        ],
        deliverables: ['Optimized POLCA system', 'Office QRM processes', 'Champion network'],
        resources: ['Office staff', 'Additional cell teams', 'QRM champions']
      },
      {
        phase: 'Phase 4: Scaling (Weeks 21-32)',
        duration: '12 weeks',
        tasks: [
          'Roll out QRM to remaining cells',
          'Implement supplier QRM principles',
          'Advanced QRM training (Silver level)',
          'Setup continuous improvement process',
          'Develop QRM culture and mindset'
        ],
        deliverables: ['Company-wide QRM system', 'Supplier agreements', 'Culture transformation'],
        resources: ['All departments', 'Supply chain team', 'Senior management commitment']
      }
    ],
    Lean: [
      {
        phase: 'Phase 1: 5S Foundation (Weeks 1-6)',
        duration: '6 weeks',
        tasks: [
          'Leadership alignment and commitment',
          'Form Lean implementation team',
          'Conduct current state assessment',
          'Implement 5S in pilot area',
          'Train employees on Lean fundamentals'
        ],
        deliverables: ['5S implemented area', 'Current state documentation', 'Training materials'],
        resources: ['Lean coordinator', 'Area supervisors', 'Hourly employees']
      },
      {
        phase: 'Phase 2: Value Stream Mapping (Weeks 7-14)',
        duration: '8 weeks',
        tasks: [
          'Create current state value stream maps',
          'Identify waste and improvement opportunities',
          'Design future state value stream',
          'Develop implementation action plan',
          'Begin kaizen events'
        ],
        deliverables: ['Value stream maps', 'Future state design', 'Kaizen action plans'],
        resources: ['Value stream team', 'Process engineers', 'Front-line workers']
      },
      {
        phase: 'Phase 3: Flow Implementation (Weeks 15-26)',
        duration: '12 weeks',
        tasks: [
          'Implement continuous flow where possible',
          'Setup pull systems and kanban',
          'Standardize work processes',
          'Implement TPM (Total Productive Maintenance)',
          'Monthly kaizen events'
        ],
        deliverables: ['Flow systems', 'Kanban cards', 'Standard work sheets'],
        resources: ['Production teams', 'Maintenance staff', 'Quality team']
      },
      {
        phase: 'Phase 4: Continuous Improvement (Weeks 27-40)',
        duration: '14 weeks',
        tasks: [
          'Expand Lean to all value streams',
          'Implement mistake-proofing (Poka-yoke)',
          'Setup supplier partnerships',
          'Advanced Lean training',
          'Sustain improvements and culture'
        ],
        deliverables: ['Lean enterprise', 'Supplier partnerships', 'Self-sustaining culture'],
        resources: ['All employees', 'Supply chain', 'Continuous improvement team']
      }
    ]
  };

  const strategies = ['QRM', 'Lean Manufacturing', 'Theory of Constraints', 'Kaizen', '5S', 'Digital Work'];
  const phases = implementationPhases[selectedStrategy] || implementationPhases['QRM'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Implementation Planning</h1>
            <p className="text-xl text-gray-600">
              Your step-by-step roadmap to manufacturing excellence
            </p>
          </div>

          {/* Strategy Selection */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Implementation Strategy</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {strategies.map((strategy) => (
                <Button
                  key={strategy}
                  variant={selectedStrategy === strategy ? "default" : "outline"}
                  onClick={() => setSelectedStrategy(strategy)}
                  className="p-4 h-auto text-left"
                >
                  <div>
                    <div className="font-semibold">{strategy}</div>
                    <div className="text-sm opacity-75 mt-1">
                      {strategy === 'QRM' && 'High-mix, low-volume focus'}
                      {strategy === 'Lean Manufacturing' && 'Waste elimination approach'}
                      {strategy === 'Theory of Constraints' && 'Bottleneck management'}
                      {strategy === 'Kaizen' && 'Continuous improvement'}
                      {strategy === '5S' && 'Workplace organization'}
                      {strategy === 'Digital Work' && 'Technology-enabled'}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </Card>

          {/* Implementation Timeline */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {selectedStrategy} Implementation Roadmap
            </h2>
            <div className="space-y-8">
              {phases.map((phase, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-6 relative">
                  {/* Phase Header */}
                  <div className="absolute w-4 h-4 bg-blue-500 rounded-full -left-2 top-2"></div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{phase.phase}</h3>
                      <Badge className="bg-blue-100 text-blue-800">{phase.duration}</Badge>
                    </div>

                    <Tabs defaultValue="tasks" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="tasks">Tasks</TabsTrigger>
                        <TabsTrigger value="deliverables">Deliverables</TabsTrigger>
                        <TabsTrigger value="resources">Resources</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="tasks" className="mt-4">
                        <ul className="space-y-2">
                          {phase.tasks.map((task, taskIndex) => (
                            <li key={taskIndex} className="flex items-start space-x-3">
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{task}</span>
                            </li>
                          ))}
                        </ul>
                      </TabsContent>
                      
                      <TabsContent value="deliverables" className="mt-4">
                        <ul className="space-y-2">
                          {phase.deliverables.map((deliverable, delIndex) => (
                            <li key={delIndex} className="flex items-start space-x-3">
                              <Target className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{deliverable}</span>
                            </li>
                          ))}
                        </ul>
                      </TabsContent>
                      
                      <TabsContent value="resources" className="mt-4">
                        <ul className="space-y-2">
                          {phase.resources.map((resource, resIndex) => (
                            <li key={resIndex} className="flex items-start space-x-3">
                              <Users className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{resource}</span>
                            </li>
                          ))}
                        </ul>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Success Metrics */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Expected Results & KPIs</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-600 mb-2">60-70%</div>
                <div className="text-sm text-gray-600">Lead Time Reduction</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">95%+</div>
                <div className="text-sm text-gray-600">Delivery Reliability</div>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <div className="text-3xl font-bold text-purple-600 mb-2">40-50%</div>
                <div className="text-sm text-gray-600">WIP Reduction</div>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="text-center space-x-4">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.location.href = '/roi-simulator'}
              className="px-8 py-3"
            >
              ← Back to ROI Simulator
            </Button>
            <Button 
              size="lg"
              className="px-8 py-3 bg-green-600 hover:bg-green-700"
              onClick={() => window.print()}
            >
              Print Implementation Plan
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="px-8 py-3"
              onClick={() => window.location.href = '/contact'}
            >
              Get Expert Guidance
            </Button>
          </div>

          {/* Expert Contact */}
          <Card className="p-8 mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Need Personalized Guidance?</h3>
              <p className="text-gray-600 mb-6">
                Get direct support from Jan Pauwels, QRM-certified expert with 38 years of manufacturing experience
              </p>
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = 'mailto:info@smartqrmnavigator.com?subject=Implementation Planning Support'}
              >
                Contact Jan Pauwels Directly
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ImplementationPlanning;
